import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { deduplicatedGet } from '../utils/deduplicatedGet';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Portal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

/** Purpose: Build blank function within the function management module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const blankFunction = () => ({ name: '', description: '', code: 'async (data) => {\n  return { success: true };\n}', inputs: [], timeout: 5000 });

/** Purpose: Load find unclosed quote line for the function management module. Why: The selected view needs current data to render or continue processing. */
const findUnclosedQuoteLine = (source = '') => {
  let quote = '';
  let line = 1;
  let escaped = false;
  for (const character of source) {
    if (character === '\n') {
      if (quote && quote !== '`') return line;
      line += 1;
      escaped = false;
      continue;
    }
    if (escaped) { escaped = false; continue; }
    if (character === '\\') { escaped = true; continue; }
    if (quote) {
      if (character === quote) quote = '';
    } else if (["'", '"', '`'].includes(character)) {
      quote = character;
    }
  }
  return quote ? line : null;
};

/** Purpose: Perform get code syntax error for the function management module. Why: Callers need a consistent result from this shared operation. */
const getCodeSyntaxError = (code = '') => {
  const source = String(code || '').trim();
  if (!source) return { message: 'Function code is required.', line: 1 };
  const unclosedQuoteLine = findUnclosedQuoteLine(source);
  if (unclosedQuoteLine) return { message: 'Unterminated string literal.', line: unclosedQuoteLine };
  const stripped = source
    .replace(/^(\s*(\/\/[^\n]*\n|\/\*[\s\S]*?\*\/)\s*)+/g, '')
    .trim();
  let unwrapped = stripped.replace(/^(?:module\.exports\s*=|export\s+default\s+)/, '').trim();
  if (unwrapped.startsWith('(') && unwrapped.endsWith(')')) {
    const inner = unwrapped.slice(1, -1).trim();
    if (
      /^(?:async\s*)?(?:\([^;]*?\)|[A-Za-z_$][\w$]*)\s*=>/.test(inner)
      || /^(?:async\s+)?function\b/.test(inner)
    ) {
      unwrapped = inner;
    }
  }
  const isFunctionLike =
    /^(?:async\s*)?(?:\([^;]*?\)|[A-Za-z_$][\w$]*)\s*=>/.test(unwrapped)
    || /^(?:async\s+)?function\b/.test(unwrapped);
  try {
    // Parse only—this never runs customer code in the browser.
    const prefix = isFunctionLike ? 'return (\n' : 'return (async function() {\n';
    new Function(`${prefix}${isFunctionLike ? unwrapped : source}\n${isFunctionLike ? ');' : '});'}`);
    return null;
  } catch (syntaxError) {
    const lineMatch = String(syntaxError.stack || '').match(/<anonymous>:(\d+):(\d+)/);
    return {
      message: syntaxError.message || 'Invalid JavaScript syntax.',
      line: lineMatch ? Math.max(1, Number(lineMatch[1]) - 1) : 1,
    };
  }
};

const JAVA_SCRIPT_TOKENS = /(\/\*[\s\S]*?\*\/|\/\/[^\n]*|`(?:\\.|[^`])*`|'(?:\\.|[^'])*'|"(?:\\.|[^"])*"|\b(?:async|await|const|let|var|return|if|else|for|while|function|new|throw|try|catch|typeof|instanceof|in|of)\b|\b(?:true|false|null|undefined)\b|\b(?:JSON|console|Math|Date|Promise|Object|Array|String|Number|Boolean|Error)\b|\.[A-Za-z_$][\w$]*|\b\d+(?:\.\d+)?\b)/g;

/** Purpose: Perform render java script for the function management module. Why: Callers need a consistent result from this shared operation. */
const renderJavaScript = (source = '') => String(source).split(JAVA_SCRIPT_TOKENS).map((token, index) => {
  if (!token) return null;
  if (/^\/\//.test(token) || /^\/\*/.test(token)) return <span key={index} className="text-slate-500">{token}</span>;
  if (/^['"`]/.test(token)) return <span key={index} className="text-amber-300">{token}</span>;
  if (/^(true|false|null|undefined)$/.test(token)) return <span key={index} className="text-violet-300">{token}</span>;
  if (token === 'typeof') return <span key={index} className="text-orange-300">{token}</span>;
  if (/^(JSON|console|Math|Date|Promise|Object|Array|String|Number|Boolean|Error)$/.test(token)) return <span key={index} className="text-sky-300">{token}</span>;
  if (/^\./.test(token)) return <span key={index} className="text-emerald-300">{token}</span>;
  if (/^\d/.test(token)) return <span key={index} className="text-orange-300">{token}</span>;
  return <span key={index} className="text-fuchsia-300">{token}</span>;
});

/** Purpose: Perform format java script source for the function management module. Why: Callers need a consistent result from this shared operation. */
const formatJavaScriptSource = (source = '') => {
  let depth = 0;
  let previousWasBlank = false;
  /** Purpose: Perform formatted for the function management module. Why: Callers need a consistent result from this shared operation. */
  const formatted = String(source).replace(/\r\n/g, '\n').split('\n').reduce((lines, rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      if (!previousWasBlank && lines.length) lines.push('');
      previousWasBlank = true;
      return lines;
    }
    previousWasBlank = false;
    const codeOnly = line
      .replace(/(['"`])(?:\\.|(?!\1)[^\\])*\1/g, '')
      .replace(/\/\/.*$/, '');
    const closingBraces = (codeOnly.match(/[}\]]/g) || []).length;
    const openingBraces = (codeOnly.match(/[{\[]/g) || []).length;
    const lineDepth = Math.max(0, depth - (codeOnly.startsWith('}') || codeOnly.startsWith(']') ? 1 : 0));
    lines.push(`${'  '.repeat(lineDepth)}${line}`);
    depth = Math.max(0, depth + openingBraces - closingBraces);
    return lines;
  }, []);
  return formatted.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

/** Purpose: Render the java script editor component. Why: This component provides the function management view used by its callers. */
function JavaScriptEditor({
  value,
  onChange,
  onSave,
  readOnly = false,
  fileName = 'function',
  expanded = false,
  onToggleExpanded,
  tabs = [],
  activeFunctionId,
  onSelectFunction,
  onCloseTab,
  onOpenFunctionPicker,
  errorBanner,
  bottomBar,
}) {
  const [scroll, setScroll] = useState({ top: 0, left: 0 });
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const editorRef = useRef(null);
  const historyRef = useRef({ undo: [], redo: [], latestValue: value });
  /** Purpose: Resolve line numbers for the function management module. Why: The surrounding operation uses this value to make its decision. */
  const lineNumbers = useMemo(() => Array.from({ length: Math.max(1, String(value || '').split('\n').length) }, (_, index) => index + 1), [value]);

  useEffect(() => {
    if (historyRef.current.latestValue !== value) {
      historyRef.current = { undo: [], redo: [], latestValue: value };
    }
  }, [value]);

  /** Purpose: Resolve commit change for the function management module. Why: The surrounding operation uses this value to make its decision. */
  const commitChange = (nextValue, selectionStart, selectionEnd) => {
    const editor = editorRef.current;
    if (nextValue === value) return;
    historyRef.current.undo.push({
      value,
      selectionStart: editor?.selectionStart ?? selectionStart ?? 0,
      selectionEnd: editor?.selectionEnd ?? selectionEnd ?? 0,
    });
    historyRef.current.redo = [];
    historyRef.current.latestValue = nextValue;
    onChange(nextValue);
  };

  /** Purpose: Resolve restore history for the function management module. Why: The surrounding operation uses this value to make its decision. */
  const restoreHistory = (direction) => {
    const editor = editorRef.current;
    const stack = direction === 'undo' ? historyRef.current.undo : historyRef.current.redo;
    const inverse = direction === 'undo' ? historyRef.current.redo : historyRef.current.undo;
    const entry = stack.pop();
    if (!entry) return;
    inverse.push({
      value,
      selectionStart: editor?.selectionStart ?? 0,
      selectionEnd: editor?.selectionEnd ?? 0,
    });
    historyRef.current.latestValue = entry.value;
    onChange(entry.value);
    requestAnimationFrame(() => {
      const active = editorRef.current;
      if (!active) return;
      active.focus();
      active.setSelectionRange(entry.selectionStart, entry.selectionEnd);
    });
  };

  /** Purpose: Handle toggle comment in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const toggleComment = (textarea) => {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const lines = value.split('\n');
    let running = 0;
    let startLine = 0;
    let endLine = 0;
    for (let index = 0; index < lines.length; index += 1) {
      const nextRunning = running + lines[index].length + 1;
      if (running <= start && start < nextRunning) startLine = index;
      if (running <= end && end <= nextRunning) {
        endLine = end === running && index > startLine ? index - 1 : index;
        break;
      }
      running = nextRunning;
    }
    const target = lines.slice(startLine, endLine + 1);
    /** Purpose: Perform should uncomment for the function management module. Why: Callers need a consistent result from this shared operation. */
    const shouldUncomment = target.every((line) => line.trim().startsWith('//'));
    const nextLines = [...lines];
    for (let index = startLine; index <= endLine; index += 1) {
      nextLines[index] = shouldUncomment ? nextLines[index].replace(/^(\s*)\/\/\s?/, '$1') : nextLines[index].replace(/^(\s*)/, '$1// ');
    }
    commitChange(nextLines.join('\n'), start, end);
  };

  /** Purpose: Resolve insert suggestion for the function management module. Why: The surrounding operation uses this value to make its decision. */
  const insertSuggestion = (suggestion) => {
    const textarea = editorRef.current;
    const start = textarea ? textarea.selectionStart : value.length;
    const end = textarea ? textarea.selectionEnd : value.length;
    const nextValue = `${value.slice(0, start)}${suggestion}${value.slice(end)}`;
    commitChange(nextValue, start + suggestion.length, start + suggestion.length);
    setSuggestionsOpen(false);
    requestAnimationFrame(() => {
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(start + suggestion.length, start + suggestion.length);
    });
  };

  /** Purpose: Perform format code for the function management module. Why: Callers need a consistent result from this shared operation. */
  const formatCode = () => {
    const formatted = formatJavaScriptSource(value);
    commitChange(formatted, 0, 0);
    setContextMenu(null);
    requestAnimationFrame(() => editorRef.current?.focus());
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#0d1117]">
      {/* IDE Tab Header */}
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-slate-800 bg-[#161b22] px-3">
        <div className="flex min-w-0 items-center overflow-x-auto gap-1">
          {tabs.map((item) => {
            const isActive = activeFunctionId === item.functionId;
            return (
              <div
                key={item.functionId}
                className={`flex h-9 shrink-0 items-center rounded-t-lg border-t-2 font-mono text-xs transition select-none ${
                  isActive
                    ? 'border-t-blue-500 bg-[#0d1117] text-white shadow-xs'
                    : 'border-t-transparent bg-transparent text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelectFunction?.(item)}
                  className="flex items-center gap-1.5 px-3 py-1.5 outline-none"
                >
                  <span className="font-bold text-amber-400 text-xs">JS</span>
                  <span className="font-medium tracking-tight">{item.name}.js</span>
                </button>
                {tabs.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseTab?.(item.functionId);
                    }}
                    className="mr-2 rounded p-0.5 text-slate-500 hover:bg-white/10 hover:text-white"
                    title="Close tab"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
          {!activeFunctionId ? (
            <div className="flex h-9 shrink-0 items-center gap-1.5 rounded-t-lg border-t-2 border-t-blue-500 bg-[#0d1117] px-3 font-mono text-xs text-white">
              <span className="font-bold text-amber-400 text-xs">JS</span>
              <span>{fileName}.js</span>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onOpenFunctionPicker}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-800 hover:text-white"
            title="Open or switch functions"
          >
            +
          </button>
        </div>

        {/* Top Quick Actions / Helpers */}
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => insertSuggestion('data.variables.')}
            className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-2.5 py-1 font-mono text-[11px] font-semibold text-cyan-300 transition hover:bg-cyan-500/20"
            title="Insert data.variables."
          >
            data.variables
          </button>
          <button
            type="button"
            onClick={() => insertSuggestion('JSON.parse()')}
            className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 px-2.5 py-1 font-mono text-[11px] font-semibold text-cyan-300 transition hover:bg-cyan-500/20"
            title="Insert JSON.parse()"
          >
            JSON.parse
          </button>
          <button
            type="button"
            onClick={formatCode}
            className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 font-mono text-[11px] font-semibold text-emerald-300 transition hover:bg-emerald-500/20"
            title="Format code cleanly"
          >
            Format
          </button>
          <button
            type="button"
            onClick={() => {
              const editor = editorRef.current;
              if (!editor) return;
              editor.focus();
              toggleComment(editor);
            }}
            className="rounded-lg border border-slate-700 bg-slate-800/80 px-2.5 py-1 font-mono text-[11px] font-medium text-slate-300 transition hover:bg-slate-700"
            title="Toggle comments (Ctrl/Cmd + /)"
          >
            // Comment
          </button>
          <button
            type="button"
            onClick={onToggleExpanded}
            className="rounded-lg border border-slate-700 bg-slate-800/80 px-2 py-1 text-xs text-slate-300 transition hover:bg-slate-700"
            title={expanded ? 'Exit fullscreen' : 'Maximize editor (Fullscreen)'}
          >
            {expanded ? '↙ Normal' : '⤢ Fullscreen'}
          </button>
        </div>
      </div>

      {/* Optional Syntax Error Banner */}
      {errorBanner}

      {/* Main Code Editing Canvas: Full Width & Full Height */}
      <div className="flex min-h-0 flex-1 relative">
        {/* Line Numbers Gutter */}
        <div className="relative w-13 shrink-0 overflow-hidden border-r border-slate-800/80 bg-[#0d1117] text-right font-mono text-[13px] leading-6 text-slate-600 select-none">
          <div className="px-3 py-4" style={{ transform: `translateY(-${scroll.top}px)` }}>
            {lineNumbers.map((line) => <div key={line}>{line}</div>)}
          </div>
        </div>

        {/* Textarea & Syntax Highlighting Canvas */}
        <div className="relative min-w-0 flex-1 overflow-hidden bg-[#0d1117]">
          {contextMenu ? (
            <div
              className="absolute z-30 w-44 overflow-hidden rounded-xl border border-slate-700 bg-[#161b22] p-1 shadow-2xl"
              style={{ left: contextMenu.x, top: contextMenu.y }}
            >
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={formatCode}
                className="block w-full rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-100 hover:bg-blue-600"
              >
                Format code
              </button>
            </div>
          ) : null}

          {suggestionsOpen ? (
            <div className="absolute right-4 top-4 z-20 w-64 overflow-hidden rounded-xl border border-slate-700 bg-[#161b22] p-1.5 shadow-2xl backdrop-blur-md">
              <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Code Snippets
              </div>
              {[
                ['system.sessionId', 'Session ID'],
                ['data.variables.', 'Flow variables'],
                ['JSON.parse()', 'Parse JSON response'],
                ['console.log()', 'Debug log'],
                ['return uniqueId;', 'Return primitive'],
                ['return { success: true };', 'Return object'],
              ].map(([suggestion, description]) => (
                <button
                  key={suggestion}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertSuggestion(suggestion)}
                  className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left font-mono text-xs text-cyan-200 hover:bg-blue-600/30 transition"
                >
                  <span className="font-semibold">{suggestion}</span>
                  <span className="font-sans text-[10px] text-slate-400">{description}</span>
                </button>
              ))}
            </div>
          ) : null}

          {/* Syntax Highlighted Code Display */}
          <pre
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 m-0 min-h-full min-w-full whitespace-pre px-5 py-4 font-mono text-[13.5px] leading-6 text-cyan-100"
            style={{ transform: `translate(${-scroll.left}px, -${scroll.top}px)` }}
          >
            {renderJavaScript(value)}
            {'\n'}
          </pre>

          {/* Actual Editable Textarea */}
          <textarea
            readOnly={readOnly}
            ref={editorRef}
            value={value}
            onChange={(event) => {
              commitChange(event.target.value, event.target.selectionStart, event.target.selectionEnd);
              const beforeCursor = event.target.value.slice(0, event.target.selectionStart);
              setSuggestionsOpen(/(?:data|JSON|console)$/.test(beforeCursor));
            }}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
                event.preventDefault();
                onSave?.();
                return;
              }
              if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
                event.preventDefault();
                restoreHistory(event.shiftKey ? 'redo' : 'undo');
                return;
              }
              if (event.ctrlKey && event.key.toLowerCase() === 'y') {
                event.preventDefault();
                restoreHistory('redo');
                return;
              }
              if ((event.metaKey || event.ctrlKey) && event.key === '/') {
                event.preventDefault();
                toggleComment(event.currentTarget);
              }
              if ((event.metaKey || event.ctrlKey) && event.code === 'Space') {
                event.preventDefault();
                setSuggestionsOpen((open) => !open);
              }
              if (event.key === 'Escape') setSuggestionsOpen(false);
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              const bounds = event.currentTarget.getBoundingClientRect();
              setContextMenu({ x: event.clientX - bounds.left, y: event.clientY - bounds.top });
            }}
            onClick={() => setContextMenu(null)}
            onScroll={(event) => setScroll({ top: event.currentTarget.scrollTop, left: event.currentTarget.scrollLeft })}
            className="absolute inset-0 h-full w-full resize-none whitespace-pre bg-transparent px-5 py-4 font-mono text-[13.5px] leading-6 text-transparent caret-cyan-300 outline-none selection:bg-blue-500/30"
            spellCheck="false"
            aria-label="Function code editor"
          />
        </div>
      </div>

      {/* Integrated IDE Bottom Status & Controls Bar */}
      {bottomBar}
    </div>
  );
}

/** Purpose: create and manage reusable custom functions. Why: flow authors need approved shared logic that can be versioned and invoked from journeys. */
export default function FunctionManagement() {
  const { selectedBot, selectedEnv } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedFunctionId = searchParams.get('functionId') || '';
  const isReadOnly = selectedEnv === 'prod';
  const [functions, setFunctions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(blankFunction());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [functionSwitcherOpen, setFunctionSwitcherOpen] = useState(false);
  const [openTabIds, setOpenTabIds] = useState([]);
  const [headerHost, setHeaderHost] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('function-management-header-actions') : null
  ));
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newFunctionName, setNewFunctionName] = useState('');

  useEffect(() => {
    if (!headerHost && typeof document !== 'undefined') {
      const element = document.getElementById('function-management-header-actions');
      if (element) setHeaderHost(element);
    }
  }, [headerHost]);

  const codeSyntaxError = getCodeSyntaxError(draft.code);
  /** Purpose: Handle open functions in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openFunctions = useMemo(() => openTabIds.map((functionId) => functions.find((item) => item.functionId === functionId)).filter(Boolean), [functions, openTabIds]);

  /** Purpose: Load load functions for the function management module. Why: The selected view needs current data to render or continue processing. */
  const loadFunctions = useCallback(async () => {
    if (!selectedBot || !selectedEnv) { setFunctions([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const environment = await deduplicatedGet(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
      const environmentId = environment.data?.environment?.envId || selectedEnv;
      const response = await deduplicatedGet(`/api/v1/function-definitions?botId=${selectedBot}&environmentId=${environmentId}`);
      const items = response.data?.functions || [];
      setFunctions(items);
      const target = requestedFunctionId ? items.find((item) => item.functionId === requestedFunctionId) : items[0];
      if (target) {
        setSelected(target);
        setDraft({ ...target, inputs: target.inputs || [] });
      } else {
        setSelected(null);
        setDraft(blankFunction());
      }
      if (requestedFunctionId && !target) {
        setError('Mapped function was not found in the selected bot/environment. Check the environment or function mapping.');
      }
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load functions.');
    } finally {
      setLoading(false);
    }
  }, [selectedBot, selectedEnv, requestedFunctionId]);

  useEffect(() => { loadFunctions(); }, [loadFunctions]);

  useEffect(() => {
    if (!selected?.functionId) return;
    setOpenTabIds((current) => (current.includes(selected.functionId) ? current : [...current, selected.functionId]));
  }, [selected?.functionId]);

  /** Purpose: Handle select function in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const selectFunction = (item) => {
    setSelected(item);
    setDraft({ ...item, inputs: item.inputs || [] });
    setError('');
    setValidationAttempted(false);
    setFunctionSwitcherOpen(false);
  };

  /** Purpose: Handle close tab in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const closeTab = (tabFunctionId) => {
    setOpenTabIds((prev) => {
      /** Purpose: Resolve next for the function management module. Why: The surrounding operation uses this value to make its decision. */
      const next = prev.filter((id) => id !== tabFunctionId);
      if (selected?.functionId === tabFunctionId) {
        const nextId = next[next.length - 1];
        /** Purpose: Resolve next fn for the function management module. Why: The surrounding operation uses this value to make its decision. */
        const nextFn = functions.find((f) => f.functionId === nextId);
        /** Purpose: Resolve remaining fns for the function management module. Why: The surrounding operation uses this value to make its decision. */
        const remainingFns = functions.filter((f) => f.functionId !== tabFunctionId);
        if (nextFn) {
          selectFunction(nextFn);
        } else if (remainingFns.length > 0) {
          selectFunction(remainingFns[0]);
        } else {
          setSelected(null);
          setDraft(blankFunction());
        }
      }
      return next;
    });
  };

  /** Purpose: Handle handle open create in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleOpenCreate = () => {
    setNewFunctionName('');
    setNewDialogOpen(true);
  };

  /** Purpose: Handle handle confirm create in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleConfirmCreate = () => {
    const cleanName = newFunctionName.trim();
    if (!cleanName) return;
    setSelected(null);
    setDraft({ ...blankFunction(), name: cleanName });
    setError('');
    setValidationAttempted(false);
    setNewDialogOpen(false);
    setNewFunctionName('');
  };

  /** Purpose: Handle handle delete in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleDelete = async (functionId) => {
    if (!functionId || isReadOnly) return;
    /** Purpose: Resolve fn to delete for the function management module. Why: The surrounding operation uses this value to make its decision. */
    const fnToDelete = functions.find((f) => f.functionId === functionId);
    const name = fnToDelete?.name || 'this function';
    if (!window.confirm(`Are you sure you want to delete function "${name}"?`)) return;
    try {
      await axios.delete(`/api/v1/function-definitions/${functionId}`, {
        params: { botId: selectedBot || fnToDelete?.botId },
      });
      setError('');
      closeTab(functionId);
      await loadFunctions();
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'Could not delete function.');
    }
  };

  /** Purpose: Apply update draft in the function management module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
  const updateDraft = (patch) => setDraft((current) => ({ ...current, ...patch }));

  /** Purpose: Handle save in the function management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const save = async () => {
    setValidationAttempted(true);
    if (!draft.name.trim() || !draft.code.trim()) {
      setError('Function name and code are required.');
      return;
    }
    if (codeSyntaxError) {
      setError(`Line ${codeSyntaxError.line}: ${codeSyntaxError.message}`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const environment = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
      const environmentId = environment.data?.environment?.envId || selectedEnv;
      const payload = {
        ...draft,
        botId: selectedBot,
        environmentId,
        inputs: (draft.inputs || []).map((input) => input.trim()).filter(Boolean),
      };
      const response = selected?.functionId
        ? await axios.put(`/api/v1/function-definitions/${selected.functionId}`, payload)
        : await axios.post('/api/v1/function-definitions', payload);
      const saved = response.data.function;
      setSelected(saved);
      setDraft({ ...saved, inputs: saved.inputs || [] });
      await loadFunctions();
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Could not save function.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${expanded ? 'fixed inset-0 z-[100] rounded-none border-0' : 'h-full w-full rounded-2xl border border-slate-800'} relative flex flex-col overflow-hidden bg-[#0d1117] shadow-2xl`}>
      {/* Function selection & New function button positioned in header next to DEV */}
      <Portal container={headerHost} disablePortal={!headerHost}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flexShrink: 0 }}>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 150, sm: 190, md: 230 },
              maxWidth: { xs: 180, sm: 250, md: 300 },
            }}
          >
            <InputLabel id="header-function-select-label" sx={{ fontSize: '0.8125rem' }}>
              Select function
            </InputLabel>
            <Select
              labelId="header-function-select-label"
              id="header-function-select"
              value={selected?.functionId || (draft.name && !selected?.functionId ? '__draft__' : '')}
              label="Select function"
              disabled={loading}
              onChange={(event) => {
                const val = event.target.value;
                if (val === '__draft__') return;
                /** Purpose: Resolve found for the function management module. Why: The surrounding operation uses this value to make its decision. */
                const found = functions.find((f) => f.functionId === val);
                if (found) selectFunction(found);
              }}
              renderValue={(val) => {
                if (val === '__draft__') {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                      <Chip label="NEW" size="small" color="warning" sx={{ height: 19, fontSize: '0.65rem', fontWeight: 800 }} />
                      <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                        {draft.name || 'Untitled function'}
                      </Typography>
                    </Box>
                  );
                }
                /** Purpose: Resolve fn for the function management module. Why: The surrounding operation uses this value to make its decision. */
                const fn = functions.find((f) => f.functionId === val);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                    <Chip
                      label="JS"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 19, fontSize: '0.65rem', fontWeight: 800, borderColor: 'primary.main' }}
                    />
                    <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                      {fn?.name || draft.name || 'Select function'}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                borderRadius: '10px',
                height: 38,
                /** Purpose: Derive the background color style for the function management view. Why: Theme and state dependent styling should be calculated from current UI context. */
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                fontSize: '0.8125rem',
                '& .MuiSelect-select': { py: 0.75 },
              }}
            >
              {functions.length === 0 && !draft.name && (
                <MenuItem value="" disabled sx={{ fontSize: '0.8125rem' }}>
                  No saved functions
                </MenuItem>
              )}
              {draft.name && !selected?.functionId && (
                <MenuItem value="__draft__" sx={{ fontSize: '0.8125rem', fontStyle: 'italic', color: 'warning.main' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="NEW" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                    <span>{draft.name} (unsaved draft)</span>
                  </Box>
                </MenuItem>
              )}
              {functions.map((item) => (
                <MenuItem key={item.functionId} value={item.functionId} sx={{ fontSize: '0.8125rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <span className="font-bold text-amber-500 text-xs">JS</span>
                    <span style={{ fontWeight: selected?.functionId === item.functionId ? 700 : 500 }}>{item.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#94a3b8' }}>Reusable</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!isReadOnly && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                borderRadius: '10px',
                height: 38,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
                px: 1.75,
                boxShadow: 'none',
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                },
              }}
            >
              New function
            </Button>
          )}
        </Box>
      </Portal>

      {/* Unified IDE Workspace Component */}
      <JavaScriptEditor
        readOnly={isReadOnly}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((value) => !value)}
        fileName={draft.name || 'function'}
        value={draft.code}
        tabs={openFunctions}
        activeFunctionId={selected?.functionId}
        onSelectFunction={selectFunction}
        onCloseTab={closeTab}
        onOpenFunctionPicker={() => setFunctionSwitcherOpen((open) => !open)}
        onSave={save}
        onChange={(code) => {
          updateDraft({ code });
          setError('');
          setValidationAttempted(false);
        }}
        errorBanner={
          error || (validationAttempted && codeSyntaxError) ? (
            <div className="flex shrink-0 items-center justify-between border-b border-rose-900/60 bg-rose-950/80 px-4 py-2 text-xs font-semibold text-rose-300 backdrop-blur-md">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse"></span>
                <span>{codeSyntaxError ? `Syntax error on line ${codeSyntaxError.line}: ${codeSyntaxError.message}` : error}</span>
              </div>
              <button
                type="button"
                onClick={() => { setError(''); setValidationAttempted(false); }}
                className="text-rose-400 hover:text-white px-2 py-0.5 rounded"
              >
                Dismiss
              </button>
            </div>
          ) : null
        }
        bottomBar={
          <div className="flex h-12 shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-800 bg-[#161b22] px-4 font-mono text-xs">
            {/* Left: Language & Code Status, Function Name & ID */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="hidden sm:flex items-center gap-2 text-slate-400 text-[11px]">
                <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_6px_#34d399]" />
                <span className="font-bold text-slate-200">JavaScript</span>
                <span className="text-slate-600">·</span>
                <span>UTF-8</span>
                <span className="text-slate-600">·</span>
                <span>Spaces: 2</span>
              </div>

              {!isReadOnly && (
                <div className="flex items-center gap-2 sm:border-l sm:border-slate-800 sm:pl-3">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 font-sans">
                    Function:
                  </span>
                  <input
                    type="text"
                    disabled={isReadOnly}
                    value={draft.name}
                    onChange={(e) => updateDraft({ name: e.target.value })}
                    placeholder="e.g. greetingMessage"
                    className="w-40 sm:w-56 rounded-lg border border-slate-700 bg-[#0d1117] px-2.5 py-1 font-mono text-xs font-semibold text-cyan-200 shadow-inner focus:border-cyan-400 focus:outline-none transition"
                  />
                  {selected?.functionId && (
                    <span className="hidden md:inline-flex items-center gap-1 rounded bg-slate-800/80 px-2 py-0.5 text-[10px] text-slate-400 font-mono">
                      ID: <code className="text-slate-300">{selected.functionId.slice(0, 8)}…</code>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Right: Actions (Keyboard hint, Delete, Save) */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="hidden lg:inline text-[11px] text-slate-500 font-sans mr-1">
                Press <kbd className="px-1.5 py-0.5 rounded bg-slate-850 border border-slate-700 font-mono text-[10px] text-slate-300">⌘S</kbd> to save
              </span>

              {selected?.functionId && !isReadOnly && (
                <button
                  type="button"
                  onClick={() => handleDelete(selected.functionId)}
                  className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3.5 py-1.5 text-xs font-semibold text-rose-400 transition hover:bg-rose-500/20 active:scale-95"
                >
                  Delete
                </button>
              )}

              {!isReadOnly && (
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !selectedBot || !selectedEnv}
                  className="rounded-lg bg-blue-600 px-5 py-1.5 text-xs font-bold text-white shadow-md shadow-blue-600/30 transition hover:bg-blue-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save function'}
                </button>
              )}
            </div>
          </div>
        }
      />

      {/* Function Switcher Popup */}
      {functionSwitcherOpen ? (
        <div className="absolute left-3 top-12 z-30 w-72 overflow-hidden rounded-xl border border-slate-700 bg-[#161b22] p-1 shadow-2xl backdrop-blur-md">
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-800">
            Select Function
          </div>
          {functions.map((item) => (
            <button
              key={item.functionId}
              type="button"
              onClick={() => selectFunction(item)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-medium transition ${
                selected?.functionId === item.functionId
                  ? 'bg-blue-600/20 text-blue-300 font-bold'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <span>{item.name}</span>
              <span className="font-mono text-[10px] text-slate-500">JS</span>
            </button>
          ))}
        </div>
      ) : null}

      {/* Create New Function Modal Dialog */}
      <Dialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: '#161b22',
            color: '#f8fafc',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#ffffff' }}>
          Create New Function
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
            Enter a function name. This function can be reused across all flows in this bot.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Function name"
            placeholder="e.g. greetingMessage"
            value={newFunctionName}
            onChange={(e) => setNewFunctionName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirmCreate();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: '#0d1117',
                color: '#ffffff',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#38bdf8' },
              },
              '& .MuiInputLabel-root': { color: '#94a3b8' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setNewDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#94a3b8' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCreate}
            disabled={!newFunctionName.trim()}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              px: 2.5,
              bgcolor: 'primary.main',
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
